import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Pill, Search, Edit, Trash2, X, Save, Plus, ChevronLeft, ChevronRight, Activity, Calendar, Clock, AlertCircle, User } from 'lucide-react';
import axios from 'axios';
import { NODE_API_URL } from '../config';

const API_URL = NODE_API_URL;

// ==========================================
// 🎨 SUB-COMPONENT: STATUS BADGE
// ==========================================
const StatusBadge = ({ status }) => {
  const styles = {
    'Processing': 'bg-blue-50 text-blue-700 border-blue-100',
    'Not Picked': 'bg-amber-50 text-amber-700 border-amber-100',
    'Refill Due': 'bg-red-50 text-red-700 border-red-100',
    'Pending': 'bg-purple-50 text-purple-700 border-purple-100',
    'Picked': 'bg-teal-50 text-teal-700 border-teal-100',
    'Delivered': 'bg-green-50 text-green-700 border-green-100'
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${styles[status] || 'bg-gray-50 text-gray-600 border-gray-100'}`}>
      {status}
    </span>
  );
};

// ==========================================
// 📊 SUB-COMPONENT: REFILLS DASHBOARD
// ==========================================
const RefillsDashboard = ({ total, processing, overdue }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4 group hover:shadow-md transition-all">
      <div className="bg-teal-600 text-white p-3 rounded-xl shadow-lg shadow-teal-100 group-hover:scale-110 transition-transform">
        <Pill size={24} />
      </div>
      <div>
        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Total Active Refills</p>
        <h3 className="text-2xl font-black text-gray-900">{total}</h3>
      </div>
    </div>
    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4 group hover:shadow-md transition-all">
      <div className="bg-blue-500 text-white p-3 rounded-xl shadow-lg shadow-blue-100 group-hover:scale-110 transition-transform">
        <Clock size={24} />
      </div>
      <div>
        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">In Processing</p>
        <h3 className="text-2xl font-black text-gray-900">{processing}</h3>
      </div>
    </div>
    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4 group hover:shadow-md transition-all">
      <div className="bg-red-500 text-white p-3 rounded-xl shadow-lg shadow-red-100 group-hover:scale-110 transition-transform">
        <AlertCircle size={24} />
      </div>
      <div>
        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">High Priority / Due</p>
        <h3 className="text-2xl font-black text-gray-900">{overdue}</h3>
      </div>
    </div>
  </div>
);

const Refills = () => {
  const [refills, setRefills] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editForm, setEditForm] = useState({ id: null, patient_name: '', ndc: '', drug_name: '', status: '', quantity: '', days_supply: '', amount: '', expiration_date: '', order_date: '' });
  const [addForm, setAddForm] = useState({ patient_id: '', rx_no: 'TBD', ndc: '', drug_name: '', quantity: '', days_supply: '', amount: '', expiration_date: '', order_date: new Date().toISOString().split('T')[0], prescriber_name: '', prescriber_phone: '' });

  const fetchRefills = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/refills`);
      if (Array.isArray(response.data)) setRefills(response.data);
      setLoading(false);
    } catch (error) { console.error(error); setLoading(false); }
  }, []);

  const fetchPatients = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/patients`);
      if (Array.isArray(res.data)) setPatients(res.data);
    } catch (error) { console.error(error); }
  }, []);

  useEffect(() => { fetchRefills(); fetchPatients(); }, [fetchRefills, fetchPatients]);

  const stats = useMemo(() => ({
    total: refills.length,
    processing: refills.filter(r => r.status === 'Processing' || r.status === 'Pending').length,
    overdue: refills.filter(r => r.status === 'Refill Due' || r.status === 'Not Picked').length
  }), [refills]);

  // 🔥 Smart Search Logic (Name, NDC, Drug, RX, Phone)
  const filteredRefills = useMemo(() => {
    const s = searchTerm.toLowerCase();
    const cleanSearch = s.replace(/\D/g, '');

    return refills.filter(item => {
      const nameMatch = (s.length >= 1 && s.length <= 2)
        ? (item.full_name || '').toLowerCase().startsWith(s)
        : (item.full_name || '').toLowerCase().includes(s);
      const drugMatch = (item.drug_name || '').toLowerCase().includes(s);
      const ndcMatch = (item.ndc || '').includes(s);
      const rxMatch = (item.rx_no || '').includes(s);
      const phoneMatch = cleanSearch && (item.prescriber_phone || '').replace(/\D/g, '').includes(cleanSearch);

      return nameMatch || drugMatch || ndcMatch || rxMatch || phoneMatch;
    }).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
  }, [refills, searchTerm]);

  const totalEntries = filteredRefills.length;
  const totalPages = entriesPerPage === 'all' ? 1 : Math.ceil(totalEntries / entriesPerPage);
  const startIndex = entriesPerPage === 'all' ? 0 : (currentPage - 1) * entriesPerPage;
  const paginatedRefills = filteredRefills.slice(startIndex, entriesPerPage === 'all' ? totalEntries : startIndex + entriesPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, entriesPerPage]);

  const handleDelete = async (id) => {
    if (!window.confirm("Permanently archive this refill requirement?")) return;
    try {
      await axios.post(`${API_URL}/prescriptions/delete`, { id });
      fetchRefills();
    } catch (error) { alert("Error deleting record"); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/prescriptions/update`, editForm);
      setShowEditModal(false);
      fetchRefills();
    } catch (error) { alert("Error updating record"); }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...addForm, status: 'Processing' };
      await axios.post(`${API_URL}/prescriptions/add`, payload);
      setShowAddModal(false);
      setAddForm({ patient_id: '', rx_no: 'TBD', ndc: '', drug_name: '', quantity: '', days_supply: '', amount: '', expiration_date: '', order_date: new Date().toISOString().split('T')[0], prescriber_name: '', prescriber_phone: '' });
      fetchRefills();
    } catch (error) { alert("Error creating requirement"); }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto min-h-screen bg-gray-50/30">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-6">
        <div>
          <h2 className="text-3xl font-black text-gray-900 flex items-center gap-3 tracking-tight">
            <div className="bg-teal-600 p-2.5 rounded-2xl text-white shadow-lg shadow-teal-100">
              <Pill size={28} />
            </div>
            Refill Summary Report
          </h2>
          <p className="text-gray-500 mt-2 text-sm font-medium">Tracking pending drug requirements and refill logistics.</p>
        </div>

        <div className="flex flex-wrap gap-3 items-center w-full md:w-auto">
          <div className="relative flex-grow">
            <Search className="absolute left-3.5 top-3 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search Patient, Drug, RX or NDC..."
              className="pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none w-full md:w-80 text-sm shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button onClick={() => setShowAddModal(true)} className="bg-gray-900 text-white px-6 py-3 rounded-xl hover:bg-teal-700 hover:shadow-lg transition-all flex items-center gap-2 text-sm font-bold shadow-md">
            <Plus size={18} /> Add Requirement
          </button>
        </div>
      </div>

      <RefillsDashboard total={stats.total} processing={stats.processing} overdue={stats.overdue} />

      {/* TABLE CONTROLS */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm gap-4">
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Display</span>
          <select
            value={entriesPerPage}
            onChange={(e) => setEntriesPerPage(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
            className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-xs font-bold focus:ring-2 focus:ring-teal-500 outline-none"
          >
            {[10, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
            <option value="all">Unlimited</option>
          </select>
          <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest">Records</span>
        </div>

        <div className="text-xs font-bold text-teal-600 bg-teal-50 px-4 py-2 rounded-full border border-teal-100">
          Showing {startIndex + 1} to {Math.min(startIndex + paginatedRefills.length, totalEntries)} of {totalEntries} Entries Found
        </div>
      </div>

      {/* MAIN DATA TABLE */}
      <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden mb-8">
        <div className="overflow-x-auto no-scrollbar">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Patient Name</th>
                <th className="px-6 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Product / NDC</th>
                <th className="px-6 py-5 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">Quantity</th>
                <th className="px-6 py-5 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">Days</th>
                <th className="px-6 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Requirement Status</th>
                <th className="px-6 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Target Date</th>
                <th className="px-6 py-5 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">Management</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan="7" className="p-20 text-center"><Activity className="animate-pulse mx-auto text-teal-600 mb-2" size={32} /><p className="font-bold text-gray-400">Loading intelligence data...</p></td></tr>
              ) : paginatedRefills.length === 0 ? (
                <tr><td colSpan="7" className="p-20 text-center"><Pill className="mx-auto text-gray-100 mb-4" size={64} /><h3 className="text-xl font-bold text-gray-800">No Pending Requirements</h3><p className="text-gray-400 mt-1">All refills are currently up to date.</p></td></tr>
              ) : (
                paginatedRefills.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/80 transition-colors group">
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="bg-white border rounded-lg p-2 text-teal-600 shadow-sm"><User size={16} /></div>
                        <div>
                          <div className="font-black text-gray-900 leading-none">{item.full_name}</div>
                          <div className="text-[10px] font-bold text-gray-400 uppercase mt-1">RX: {item.rx_no}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="font-bold text-gray-800">{item.drug_name}</div>
                      <div className="text-[10px] font-bold text-gray-400 tracking-tighter mt-0.5">{item.ndc || 'NDC-UNKNOWN'}</div>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className="bg-gray-100 px-3 py-1 rounded-lg text-xs font-black text-gray-700">{item.quantity}</span>
                    </td>
                    <td className="px-6 py-5 text-center">
                      <span className="text-xs font-bold text-gray-500">{item.days_supply || '-'} DYS</span>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2 text-xs font-bold text-red-600">
                        <Calendar size={12} /> {item.expiration_date && item.expiration_date !== '0000-00-00' ? new Date(item.expiration_date).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex justify-center gap-2 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => {
                          setEditForm({
                            id: item.id, patient_name: item.full_name, ndc: item.ndc || '', drug_name: item.drug_name,
                            status: item.status || 'Processing', quantity: item.quantity, days_supply: item.days_supply,
                            amount: item.amount, expiration_date: item.expiration_date, order_date: item.order_date
                          });
                          setShowEditModal(true);
                        }} className="bg-white border border-gray-100 text-teal-600 p-2 rounded-xl shadow-sm hover:border-teal-400 hover:shadow-md transition-all"><Edit size={16} /></button>
                        <button onClick={() => handleDelete(item.id)} className="bg-white border border-gray-100 text-red-500 p-2 rounded-xl shadow-sm hover:border-red-400 hover:shadow-md transition-all"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINATION */}
      {entriesPerPage !== 'all' && totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 pb-10">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="p-3 bg-white border border-gray-100 rounded-2xl shadow-sm hover:bg-gray-50 disabled:opacity-40 transition-all font-bold text-xs"
          >
            <ChevronLeft size={16} />
          </button>

          <div className="flex gap-2">
            {[...Array(totalPages)].map((_, idx) => {
              const pageNum = idx + 1;
              const active = currentPage === pageNum;

              // Patients.jsx Logic: Only show first, last, and current neighborhood
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-10 h-10 rounded-2xl border font-black text-xs transition-all 
                        ${active ? 'bg-teal-600 text-white border-teal-600 shadow-lg shadow-teal-100' : 'bg-white border-gray-100 text-gray-500 hover:bg-gray-50'}`}
                  >
                    {pageNum}
                  </button>
                );
              } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                return <span key={pageNum} className="text-gray-300 px-1 self-center font-black">...</span>;
              }
              return null;
            })}
          </div>

          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="p-3 bg-white border border-gray-100 rounded-2xl shadow-sm hover:bg-gray-50 disabled:opacity-40 transition-all font-bold text-xs"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* ADD MODAL */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[550px] p-8 relative">
            <button onClick={() => setShowAddModal(false)} className="absolute top-6 right-6 text-gray-400 hover:text-gray-600"><X size={24} /></button>
            <div className="flex items-center gap-4 mb-8">
              <div className="bg-teal-600 text-white p-2.5 rounded-xl shadow-lg"><Plus size={20} /></div>
              <h3 className="text-2xl font-black text-gray-900">Add Requirement</h3>
            </div>

            <form onSubmit={handleAddSubmit} className="space-y-5">
              <div>
                <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Patient Selection</label>
                <select className="w-full bg-gray-50 border border-gray-200 p-3.5 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none font-bold text-sm cursor-pointer appearance-none" required value={addForm.patient_id} onChange={e => setAddForm({ ...addForm, patient_id: e.target.value })}>
                  <option value="">-- Choose Patient --</option>
                  {patients.map(p => (<option key={p.id} value={p.id}>{p.full_name}</option>))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Drug NDC</label>
                  <input type="text" placeholder="00000-0000-00" className="w-full bg-gray-50 border border-gray-200 p-3.5 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none text-sm font-bold" value={addForm.ndc} onChange={e => setAddForm({ ...addForm, ndc: e.target.value })} />
                </div>
                <div>
                  <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Drug Name</label>
                  <input type="text" placeholder="Drug/Medication" className="w-full bg-gray-50 border border-gray-200 p-3.5 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none text-sm font-bold" required value={addForm.drug_name} onChange={e => setAddForm({ ...addForm, drug_name: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Quantity</label>
                  <input type="number" className="w-full bg-gray-50 border border-gray-200 p-3.5 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none text-sm font-bold" value={addForm.quantity} onChange={e => setAddForm({ ...addForm, quantity: e.target.value })} />
                </div>
                <div>
                  <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Days Supply</label>
                  <input type="number" className="w-full bg-gray-50 border border-gray-200 p-3.5 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none text-sm font-bold" value={addForm.days_supply} onChange={e => setAddForm({ ...addForm, days_supply: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-black text-red-500 uppercase tracking-widest mb-2">Target Refill Date</label>
                  <input type="date" className="w-full bg-gray-50 border border-gray-200 p-3.5 rounded-xl focus:ring-2 focus:ring-red-500 outline-none font-bold" value={addForm.expiration_date} onChange={e => setAddForm({ ...addForm, expiration_date: e.target.value })} />
                </div>
                <div>
                  <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Order Date</label>
                  <input type="date" className="w-full bg-gray-50 border border-gray-200 p-3.5 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none font-bold" value={addForm.order_date} onChange={e => setAddForm({ ...addForm, order_date: e.target.value })} />
                </div>
              </div>
              <button type="submit" className="w-full bg-gray-900 text-white font-black py-4 rounded-2xl hover:bg-teal-600 shadow-xl transition-all">Create Intelligence Report</button>
            </form>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[500px] p-8 relative">
            <button onClick={() => setShowEditModal(false)} className="absolute top-6 right-6 text-gray-400 hover:text-gray-600"><X size={24} /></button>
            <div className="flex items-center gap-4 mb-8">
              <div className="bg-blue-600 text-white p-2.5 rounded-xl shadow-lg shadow-blue-100"><Edit size={20} /></div>
              <h3 className="text-2xl font-black text-gray-900">Update Requirement</h3>
            </div>
            <form onSubmit={handleUpdate} className="space-y-5">
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Managed Patient</label>
                <input type="text" className="w-full bg-gray-50 border border-transparent p-4 rounded-xl font-black text-gray-500" value={editForm.patient_name} disabled />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-1">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Drug Selection</label>
                  <input type="text" className="w-full bg-gray-50 border border-gray-200 p-4 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold" value={editForm.drug_name} onChange={e => setEditForm({ ...editForm, drug_name: e.target.value })} />
                </div>
                <div className="col-span-1">
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Status Controller</label>
                  <select className="w-full bg-gray-50 border border-gray-200 p-4 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold" value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                    <option value="Processing">Processing</option>
                    <option value="Not Picked">Not Picked</option>
                    <option value="Picked">Picked</option>
                    <option value="Delivered">Delivered</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-red-500 uppercase tracking-widest mb-2">Refill Timeline</label>
                  <input type="date" className="w-full bg-gray-50 border border-gray-200 p-4 rounded-xl focus:ring-2 focus:ring-red-500 outline-none font-bold" value={editForm.expiration_date} onChange={e => setEditForm({ ...editForm, expiration_date: e.target.value })} />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-teal-600 uppercase tracking-widest mb-2">Order Origination</label>
                  <input type="date" className="w-full bg-gray-50 border border-gray-200 p-4 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none font-bold" value={editForm.order_date} onChange={e => setEditForm({ ...editForm, order_date: e.target.value })} />
                </div>
              </div>
              <button type="submit" className="w-full bg-gray-900 text-white font-black py-4 rounded-2xl hover:bg-blue-600 shadow-xl transition-all flex items-center justify-center gap-3 mt-4">
                <Save size={20} /> Deploy Update
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Refills;